import React from 'react';

const PlanChangeConfirmModal = ({ 
  isOpen, 
  onClose, 
  currentPlan, 
  newPlan, 
  billingInfo,
  onConfirm 
}) => {
  if (!isOpen) return null;

  const PLAN_NAMES = {
    free: '無料プラン',
    standard: 'スタンダードプラン',
    premium: 'プレミアムプラン',
    premium_plus: 'プレミアムプラスプラン',
    ultimate: 'アルティメットプラン'
  };

  const PLAN_PRICES = {
    free: 0,
    standard: 480,
    premium: 980,
    premium_plus: 1980,
    ultimate: 2980
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  };

  const currentPrice = PLAN_PRICES[currentPlan];
  const newPrice = PLAN_PRICES[newPlan];
  const nextBillingDate = formatDate(billingInfo?.nextBillingDate);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 pb-24">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[75vh] overflow-y-auto">
        <div className="p-6">
          {/* ヘッダー */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">
              {newPlan === 'free' ? '解約の確認' : 'プラン変更の確認'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* 警告アイコン */}
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          </div>

          {/* 課金情報の説明 */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-yellow-800 mb-3">
              {newPlan === 'free' ? '⚠️ 解約について' : '⚠️ 課金タイミング'}
            </h4>

            {/* 現在のプラン情報 */}
            <div className="space-y-1.5 mb-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">現在:</span>
                <span className="font-medium">{PLAN_NAMES[currentPlan]} ({currentPrice}円/月)</span>
              </div>
              {nextBillingDate && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">次回更新日:</span>
                  <span className="font-medium">{nextBillingDate}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{newPlan === 'free' ? '解約後:' : '変更後:'}</span>
                <span className="font-medium text-red-600">{PLAN_NAMES[newPlan]} ({newPrice}円/月)</span>
              </div>
            </div>

            {/* 課金の説明 */}
            <div className="bg-white border border-yellow-300 rounded-lg p-2.5">
              <p className="text-sm text-gray-800">
                {newPlan === 'free' ? (
                  <>今すぐ解約すると、即座に無料プランに戻ります</>
                ) : (
                  <>今すぐ変更すると <span className="font-bold text-red-600">{newPrice}円</span> が即座に請求され、即時適用されます</>
                )}
              </p>
            </div>
          </div>

          {/* 推奨タイミング */}
          {nextBillingDate && currentPlan !== 'free' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-blue-800 mb-2">💡 おすすめのタイミング</h4>
              <p className="text-sm text-blue-700">
                {nextBillingDate}の直前に{newPlan === 'free' ? '解約' : '変更'}すると、重複課金を避けてお得です。
              </p>
            </div>
          )}

          {/* 確認ボタン */}
          <div className="flex flex-col space-y-3">
            <button
              onClick={() => onConfirm(true)}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg transition-colors"
            >
              {newPlan === 'free' ? (
                <>理解しました。今すぐ解約する</>
              ) : newPrice > 0 ? (
                <>理解しました。今すぐ変更する ({newPrice}円 即座請求)</>
              ) : (
                <>理解しました。今すぐ変更する</>
              )}
            </button>
            
            <button
              onClick={onClose}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-4 rounded-lg transition-colors"
            >
              キャンセル
            </button>
          </div>

          {/* 注意事項 */}
          <div className="mt-4 text-xs text-gray-500 text-center">
            <p>{newPlan === 'free' ? '解約後は無料プランの制限が適用されます' : '変更後は新しい課金サイクルが開始されます'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanChangeConfirmModal;